import { useEffect, useMemo, useState } from "react";
import { markdownToHtml, parseFrontMatter, type ParsedMarkdown } from "../lib/markdown";
import { useTheme } from "../lib/useTheme";
import Link from "../components/Link";
import { router } from "../lib/router";

type PageSpec = {
  id: string;
  title: string;
  path: string; // hash path, e.g. #/blog/... or #/docs/...
  loader: () => Promise<{ default: string }>;
};

const pages: PageSpec[] = [
  // Docs
  {
    id: "docs-getting-started",
    title: "Docs: Getting Started",
    path: "#/docs/getting-started",
    loader: () => import("./docs/getting-started.md?raw"),
  },
  {
    id: "docs-export-options",
    title: "Docs: Export Options",
    path: "#/docs/export-options",
    loader: () => import("./docs/export-options.md?raw"),
  },
  {
    id: "docs-brand-logo-overlay",
    title: "Docs: Brand/Logo Overlay",
    path: "#/docs/brand-logo-overlay",
    loader: () => import("./docs/brand-logo-overlay.md?raw"),
  },
  {
    id: "docs-privacy",
    title: "Docs: Privacy",
    path: "#/docs/privacy",
    loader: () => import("./docs/privacy.md?raw"),
  },
];

function useRouter() {
  const [path, setPath] = useState<string>(() => router.getCurrentPath());
  
  useEffect(() => {
    return router.subscribe((newPath) => {
      setPath(newPath);
    });
  }, []);
  
  return path;
}

function findPageByPath(path: string): PageSpec | undefined {
  if (!path) return undefined;
  // Convert paths to be compatible with page specs
  const hashPath = path.startsWith('/') ? `#${path}` : `#/${path}`;
  return pages.find((p) => hashPath.startsWith(p.path) || path.startsWith(p.path.replace('#', '')));
}

export default function ContentRouter() {
  const path = useRouter();
  const active = useMemo(() => findPageByPath(path), [path]);
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState<ParsedMarkdown | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!active) {
        setParsed(null);
        return;
      }
      setLoading(true);
      try {
        const raw = await active.loader();
        if (cancelled) return;
        const parsed = parseFrontMatter(raw.default);
        setParsed(parsed);
        // SEO
        if (parsed.frontMatter.title) document.title = parsed.frontMatter.title + " — PrintQR";
        if (parsed.frontMatter.description) setMetaDescription(parsed.frontMatter.description);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active) return null;
  if (loading || !parsed) return <PageChrome title="Loading…"><div>Loading…</div></PageChrome>;

  const html = markdownToHtml(parsed.body);
  const title = parsed.frontMatter.title || active.title;
  return (
    <PageChrome title={title}>
      <article dangerouslySetInnerHTML={{ __html: html }} />
    </PageChrome>
  );
}

function PageChrome({ title, children }: { title: string; children: React.ReactNode }) {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <div style={{ padding: 24, maxWidth: 860, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ color: "var(--accent)", textDecoration: "none" }}>&larr; Home</Link>
        <button 
          onClick={toggleTheme} 
          className="btn btn-icon" 
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          style={{ 
            width: "2.5rem", 
            height: "2.5rem", 
            borderRadius: "0.75rem", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            border: "1px solid var(--border)",
            background: "transparent",
            cursor: "pointer"
          }}
        >
          {theme === 'light' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4"></circle>
              <path d="M12 2v2"></path>
              <path d="M12 20v2"></path>
              <path d="m4.93 4.93 1.41 1.41"></path>
              <path d="m17.66 17.66 1.41 1.41"></path>
              <path d="M2 12h2"></path>
              <path d="M20 12h2"></path>
              <path d="m6.34 17.66-1.41 1.41"></path>
              <path d="m19.07 4.93-1.41 1.41"></path>
            </svg>
          )}
        </button>
      </div>
      <h1 style={{ marginTop: 12 }}>{title}</h1>
      <div style={{ height: 1, background: "var(--border)", margin: "12px 0 24px" }} />
      {children}
    </div>
  );
}

function setMetaDescription(content: string) {
  let el = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "description");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}


